import Filter from '../inputs/filter'
import { MobileFilter } from '../inputs'
import Tabs from '../tabs/tabs'
import Timeline from '../timeline/timeline'
import Counter from '../badgesAndTags/counter'
import TypographyHeading from '../typography/TypographyHeading'
import { useState, useLayoutEffect } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import MoreIcon from '../icons/Interface/MoreIcon'
import IconButton from '../buttons/iconButton'
import { useTheme } from '#app/utils/providers/themeProvider'
import { Button } from '../buttons'
import { SignedIcon } from '../icons'
import { TypographyParagraph } from '../typography'
import { Callout } from '../callout'

export default function OffChain({
	timeline,
	totalMetadata,
	atomicLoreUrl,
	subtitle,
	noContentText,
	usePagination = true,
	showSignButton = false,
	isPreview = false,
	setSignModalOpen,
	onHighlightAttestation,
	highlightedAttestations = [],
}: {
	timeline: any[]
	totalMetadata?: number
	atomicLoreUrl?: string
	subtitle?: string
	noContentText?: string
	usePagination?: boolean
	showSignButton?: boolean
	isPreview?: boolean
	setSignModalOpen?: (open: boolean) => void
	onHighlightAttestation?: (
		attestationIPFSCID: string,
		userId: string,
	) => Promise<void>
	highlightedAttestations?: any[]
}) {
	const compareDates = (a, b) => {
		const aComparisonDate = a.comparisonDate || a.createdAt
		const bComparisonDate = b.comparisonDate || b.createdAt
		const dateA = new Date(aComparisonDate)
		const dateB = new Date(bComparisonDate)

		switch (sortCriteria.description) {
			case 'Newest':
				return dateB - dateA
			case 'Oldest':
				return dateA - dateB
			default:
				return dateB - dateA
		}
	}

	const options = [
		{ title: 'All Types', description: 'All Types' },
		{ title: 'Proof of Exhibition', description: 'proofOfExhibition' },
		{ title: 'Media Citation', description: 'mediaCitation' },
		{ title: 'Media Upload', description: 'media' },
		{ title: 'Comment', description: 'comment' },
		{ title: 'Tweet', description: 'tweet' },
		{ title: 'Cast', description: 'cast' },
		// { title: 'Instagram', description: 'instagram' },
		{ title: 'Offchain Lend', description: 'offchainLend' },
		{ title: 'Offchain Sale', description: 'offchainSale' },
		{ title: 'Collection', description: 'collection' },
	]
	const sort = [
		{ title: 'Newest', description: 'Newest' },
		{ title: 'Oldest', description: 'Oldest' },
	]

	const [sortCriteria, setSortCriteria] = useState({
		title: 'Newest',
		description: 'Newest',
	})
	const [filterCriteria, setFilterCriteria] = useState({
		title: 'All Types',
		description: 'All Types',
	})

	const [currentPage, setCurrentPage] = useState(1)
	const entriesPerPage = 10

	const timelineYears = getYears(timeline)

	const timelineOptions = options.map(option => {
		if (option.description === 'All Types') {
			return {
				title: option.title,
				optionCount: timeline.length,
				description: option.description,
			}
		}
		const optionCount = timeline.filter(
			attestation => attestation.template.slug === option.description,
		).length
		return {
			title: option.title,
			optionCount: optionCount,
			description: option.description,
		}
	})

	const [selectedYearIndex, setSelectedYearIndex] = useState(
		timeline ? getDefaultYear(timeline) : 0,
	)

	const handleUpdateSelectedYear = (selectedIndex: number) => {
		setSelectedYearIndex(selectedIndex)
		setCurrentPage(1)
		setPageCount(calculatePageCount(filterCriteria, selectedIndex))
	}

	const getEntriesForSelectedYear = (yearIndex: number) => {
		const currentYear = timelineYears[yearIndex]
		if (currentYear) {
			const currentYearEntries = timeline.filter(timelineEntry =>
				timelineEntry.comparisonDate
					? currentYear == new Date(timelineEntry.comparisonDate).getFullYear()
					: currentYear == new Date(timelineEntry.createdAt).getFullYear(),
			)
			return currentYearEntries
		} else {
			return timeline
		}
	}

	const calculatePageCount = (
		val: { title: string; description: string },
		timelineYearIndex: number,
	) => {
		const selectedYearTimelineEntries =
			getEntriesForSelectedYear(timelineYearIndex)
		if (val.description === 'All Types') {
			return selectedYearTimelineEntries.length > entriesPerPage
				? Math.ceil(selectedYearTimelineEntries.length / entriesPerPage)
				: 1
		} else {
			const filteredLength = selectedYearTimelineEntries.filter(
				attestation => attestation.template.slug === val.description,
			)
			return Math.ceil(filteredLength.length / entriesPerPage)
		}
	}

	const [pageCount, setPageCount] = useState(
		getEntriesForSelectedYear(selectedYearIndex).length > entriesPerPage
			? Math.ceil(
					getEntriesForSelectedYear(selectedYearIndex).length / entriesPerPage,
				)
			: 1,
	)

	const handleFilterChange = (val: { title: string; description: string }) => {
		setFilterCriteria(val)
		const newPageCount = calculatePageCount(val, selectedYearIndex)
		setCurrentPage(1)
		setPageCount(newPageCount)
	}

	return (
		<div className="mt-[80px] flex w-full flex-col px-0 md:mt-24 lg:px-12">
			<div className="flex w-full flex-row items-center justify-between">
				<div className="flex-start flex flex-col items-start justify-start">
					<div className="flex flex-1 items-center gap-x-4">
						<TypographyHeading
							size="medium"
							isBold={true}
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							Onchain History
						</TypographyHeading>
						<Counter size="small">
							{totalMetadata ? totalMetadata : timeline.length}
						</Counter>
					</div>
					{subtitle && (
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
						>
							{subtitle}
						</TypographyParagraph>
					)}
				</div>
				<div className="flex flex-1 flex-row items-center justify-end gap-x-4">
					{/* Mobile filter - visible only on small screens */}
					<div className="sm:hidden">
						<MobileFilter
							sortOptions={sort}
							filterOptions={timelineOptions}
							onSortChange={newVal => setSortCriteria(newVal)}
							onFilterChange={newVal => handleFilterChange(newVal)}
							selectedSort={sortCriteria}
							selectedFilter={filterCriteria}
						/>
					</div>
					{/* Desktop filters - hidden on small screens */}
					<div className="hidden sm:block">
						<Filter
							type="Filter"
							options={timelineOptions}
							onChange={newVal => handleFilterChange(newVal)}
							value={filterCriteria}
						/>
					</div>
					<div className="hidden sm:block">
						<Filter
							type="Sort"
							options={sort}
							onChange={newVal => setSortCriteria(newVal)}
							value={sortCriteria}
						/>
					</div>
					{showSignButton && setSignModalOpen && (
						<Button
							variant="primary"
							size="medium"
							leftIcon={<SignedIcon />}
							onClick={() => setSignModalOpen(true)}
							additionalClasses="hidden sm:flex"
						>
							Sign
						</Button>
					)}
				</div>
			</div>
			<div className="flex flex-row pt-12 dark:border-uiTertiaryDark">
				<TabsAndTimeline
					timeline={timeline}
					selectedYearIndex={selectedYearIndex}
					setSelectedYearIndex={handleUpdateSelectedYear}
					compareDates={compareDates}
					timelineYears={timelineYears}
					filterCriteria={filterCriteria}
					atomicLoreUrl={atomicLoreUrl}
					noContentText={noContentText}
					currentPage={currentPage}
					entriesPerPage={entriesPerPage}
					usePagination={usePagination}
					isPreview={isPreview}
					onHighlightAttestation={onHighlightAttestation}
					highlightedAttestations={highlightedAttestations}
				/>
			</div>
			{usePagination && currentPage < pageCount && (
				<div className="flex w-full justify-center">
					<IconButton
						additionalClasses="mt-12 h-10 w-14 border-solid px-4 py-2 rounded-full"
						disabled={false}
						icon={<MoreIcon />}
						onClick={() => {
							setCurrentPage(currentPage + 1)
						}}
					/>
				</div>
			)}
		</div>
	)
}

const canUseDOM = !!(
	typeof window !== 'undefined' &&
	window.document &&
	window.document.createElement
)

const safeUseLayoutEffect = canUseDOM ? useLayoutEffect : () => {}

const TabsAndTimeline = ({
	timeline,
	selectedYearIndex,
	setSelectedYearIndex,
	compareDates,
	timelineYears,
	filterCriteria,
	atomicLoreUrl,
	noContentText,
	currentPage,
	entriesPerPage,
	usePagination = true,
	isPreview = false,
	onHighlightAttestation,
	highlightedAttestations = [],
}: {
	timeline: any[] | undefined
	selectedYearIndex: number
	setSelectedYearIndex: (index: number) => void
	compareDates: (a: any, b: any) => number
	timelineYears: number[]
	filterCriteria: { title: string; description: string }
	atomicLoreUrl: string | undefined
	noContentText: string | undefined
	currentPage: number
	entriesPerPage: number
	usePagination?: boolean
	isPreview?: boolean
	onHighlightAttestation?: (
		attestationIPFSCID: string,
		userId: string,
	) => Promise<void>
	highlightedAttestations?: any[]
}) => {
	const [theme] = useTheme()

	const [isMedium, setIsMedium] = useState(undefined)
	safeUseLayoutEffect(() => {
		function updateSize() {
			if (window.innerWidth <= 768) {
				setIsMedium(true)
			} else {
				setIsMedium(false)
			}
		}
		window && updateSize()
		window && window.addEventListener('resize', updateSize)
		return () => window && window.removeEventListener('resize', updateSize)
	}, [])

	const enhancedTimelineYears = () => {
		if (!timeline || timeline.length === 0) {
			return []
		}

		const timelineDataYears = Array.from(
			new Set(
				timeline.map(event => {
					const date = event.comparisonDate || event.createdAt
					return new Date(date).getFullYear()
				}),
			),
		)

		const oldestYear = Math.min(...timelineDataYears)

		const years = []
		const sortedYears = timelineDataYears.sort((a, b) => b - a)

		for (let i = 0; i < sortedYears.length; i++) {
			const current = sortedYears[i]
			const next = sortedYears[i + 1]

			years.push(current)

			if (next !== undefined) {
				const gap = current - next

				if (gap === 2) {
					years.push(current - 1)
				} else if (gap > 2) {
					years.push('...')
				}
			}
		}

		const lastItem = years[years.length - 1]
		if (typeof lastItem === 'number' && lastItem !== oldestYear - 1) {
			years.push(oldestYear - 1)
		}

		return years
	}

	return (
		<Tabs
			tabType={isMedium ? 'DefaultMedium' : 'Vertical'}
			defaultTab={selectedYearIndex}
			additionalClasses="lg:mr-28 lg:mr-52"
			onChange={(newIndex: number) => setSelectedYearIndex(newIndex)}
			tabOptions={enhancedTimelineYears().map(item => {
				if (item === '...') {
					return {
						title: '...',
						disabled: true,
						child: (
							<div className="text-gray-400 pt-8 text-center">
								No data for these years
							</div>
						),
					}
				}

				const year = item

				const yearEntries = timeline
					? timeline.filter(timelineElem => {
							const comparisonDate =
								timelineElem.comparisonDate || timelineElem.createdAt
							return year == new Date(comparisonDate).getFullYear()
						})
					: []

				return {
					title: `${year.toString()} `,
					titleCaption: `(${yearEntries.length})`,
					child: (
						<div key={`${year}`} className="pl-0 md:pl-8 lg:pl-16">
							{isPreview && (
								<Callout
									variant="warning"
									additionalClasses="w-full"
									content="After signing, this collection will be stored onchain. Future attestations will be recorded in the collection's onchain history."
								/>
							)}
							{!isPreview && timeline ? (
								<Timeline
									metadataEntries={yearEntries
										.filter(timelineElem =>
											filterCriteria.description === 'All Types'
												? true
												: timelineElem.template.slug ===
													filterCriteria.description,
										)
										.sort(compareDates)
										.slice(
											0,
											usePagination
												? entriesPerPage * currentPage
												: timeline.length - 1,
										)}
									metadataAdditionalClasses="w-full md:4/5"
									tweetsUrl={`${atomicLoreUrl}/getTweet`}
									noContentText={noContentText}
									displayAvatars={true}
									onHighlightAttestation={onHighlightAttestation}
									highlightedAttestations={highlightedAttestations}
								/>
							) : null}
							{!isPreview && !timeline && (
								<SkeletonTheme
									baseColor={
										theme === 'light' || theme === undefined
											? '#f5f5f5'
											: '#383838'
									}
									highlightColor={
										theme === 'light' || theme === undefined
											? '#f8f8f8'
											: '#212121'
									}
								>
									<Skeleton height={300} width={'100%'} count={5} />
								</SkeletonTheme>
							)}
						</div>
					),
				}
			})}
		/>
	)
	// }
}

const getYears = (timeline: any[]) => {
	const currentYear = new Date().getFullYear()

	if (!timeline || !timeline.length) return [currentYear, currentYear - 1]

	// Extract years with data
	const yearsWithData = timeline.map(event => {
		const dateStr = event.comparisonDate || event.createdAt
		return new Date(dateStr).getFullYear()
	})

	const dataYearSet = new Set(yearsWithData)
	dataYearSet.add(currentYear) // Always include current year

	const minYear = Math.min(...dataYearSet)
	const maxYear = Math.max(currentYear, ...dataYearSet)

	const resultYears = new Set<number>()

	for (let year = maxYear; year >= minYear; year--) {
		if (dataYearSet.has(year)) {
			resultYears.add(year)
		} else {
			// Check if this is the most recent empty year in a block
			const prevHasData = dataYearSet.has(year + 1)
			const nextHasData = dataYearSet.has(year - 1)

			if (prevHasData && !nextHasData) {
				resultYears.add(year)
			}
		}
	}

	// Add an extra empty year before the oldest data year
	if (!resultYears.has(minYear - 1)) {
		resultYears.add(minYear - 1)
	}

	return Array.from(resultYears).sort((a, b) => b - a)
}

const getDefaultYear = (timeline: any[]) => {
	const yearsArray = getYears(timeline)
	// Get the full year for each event in the timeline
	const yearsWithMetadataArray = timeline.map(event => {
		if (event.comparisonDate) {
			return new Date(event.comparisonDate).getFullYear()
		} else {
			return new Date(event.createdAt).getFullYear()
		}
	})
	// Find the most recent year with entries
	const recentYear = Math.max(...yearsWithMetadataArray)
	// Return the first year with data
	return recentYear && yearsArray.findIndex(elem => elem == recentYear) !== -1
		? yearsArray.findIndex(elem => elem == recentYear)
		: 0
}
